import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/index.js';
import type { BacklogTicket, BoardTicket, SprintBacklog, SprintLoad, SprintBoard, SprintCell, SprintPerson, SprintTicket, SprintView } from '../api/types.js';
import { useSprint, useSprintAnalyses, useSprintById, useSprintSummary } from '../hooks/use-sprint.js';
import { IncomingTickets, TicketAnalysis } from './jira-analyses.js';
import { SprintPlanSection } from './sprint-plan.js';
import type { JiraAnalysis } from '../api/types.js';
import { byUrgency, urgencyOf, widestUrgentPriority, type Urgency } from '../utils/urgency.js';

// The team's Jira sprint (ADR-034 section 2): per board, the active sprint, one row per person in the team's own
// columns, story points first and tickets in parentheses, the totals, the sprint closed last under it, then the tickets
// without estimate and the blocked ones. Computed by the API from Jira; read several times a week, so dense and plain.
// The one device: a thin bar in each Total cell, the share of each column in that person's points.

const TONES = ['var(--color-success)', 'var(--color-brand)', 'var(--color-warn)', 'var(--color-text-3)'];
const OTHER_TONE = 'var(--color-border-2)';
/** The rows a list shows before « Show all » */
const SHOWN = 50;
const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

function daysLeft(end?: string): string | null {
  if (!end) return null;
  const days = Math.ceil((Date.parse(end) - Date.now()) / 86_400_000);
  return days > 1 ? `${days} days left` : days === 1 ? '1 day left' : days === 0 ? 'ends today' : 'past its end date';
}

type Pick = (person: string, column: string) => void;

/** A number of the table; with `onPick`, a button that shows its tickets */
function Cell({ c, strong = false, label, onPick }: { c: SprintCell; strong?: boolean; label?: string; onPick?: () => void }) {
  if (!c.count) return <td className="px-3 py-1.5 text-right tabular-nums" style={{ color: 'var(--color-text-3)' }}>–</td>;
  const value = (
    <>
      <span className={strong ? 'font-semibold' : undefined} style={{ color: 'var(--color-text)' }}>{c.points}</span>
      <span style={{ color: 'var(--color-text-3)' }}> ({c.count})</span>
    </>
  );
  return (
    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
      {onPick
        ? <button type="button" onClick={onPick} aria-label={label} className="rounded underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2" style={{ outlineColor: 'var(--color-brand)' }}>{value}</button>
        : value}
    </td>
  );
}

/** The share of each column in a person's points */
function Share({ p, withOther }: { p: SprintPerson; withOther: boolean }) {
  const total = p.total.points;
  if (!total) return null;
  const parts = [...p.cells.map((c, i) => ({ points: c.points, tone: TONES[i % TONES.length]! })), ...(withOther ? [{ points: p.other.points, tone: OTHER_TONE }] : [])];
  return (
    <span aria-hidden="true" className="mt-1 flex h-1 w-full min-w-16 overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-surface-2)' }}>
      {parts.map((part, i) => part.points > 0 && <span key={i} style={{ width: `${(part.points / total) * 100}%`, backgroundColor: part.tone }} />)}
    </span>
  );
}

function Row({ p, columns, withOther, total = false, onPick }: { p: SprintPerson; columns: string[]; withOther: boolean; total?: boolean; onPick?: Pick }) {
  const person = total ? '' : p.name;
  const pick = (column: string) => (onPick ? () => onPick(person, column) : undefined);
  const label = (column: string) => `Tickets of ${total ? 'everyone' : p.name}${column ? ` in ${column}` : ''}`;
  return (
    <tr className="border-t" style={{ borderColor: total ? 'var(--color-border-2)' : 'var(--color-border)' }}>
      <th scope="row" className={`py-1.5 pr-3 text-left whitespace-nowrap ${total ? 'font-semibold' : 'font-normal'}`} style={{ color: 'var(--color-text)' }}>{p.name}</th>
      {columns.map((name, i) => <Cell key={name} c={p.cells[i]!} strong={total} label={label(name)} onPick={pick(name)} />)}
      {withOther && <Cell c={p.other} strong={total} label={label('Other')} onPick={pick('Other')} />}
      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
        <span className="font-semibold" style={{ color: 'var(--color-text)' }}>{p.total.points}</span>
        <span style={{ color: 'var(--color-text-3)' }}> ({p.total.count})</span>
        <Share p={p} withOther={withOther} />
      </td>
    </tr>
  );
}

function Ticket({ t, site, now, action }: { t: SprintTicket; site: string; now: number; action?: React.ReactNode }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 py-1">
      <a href={`${site}/browse/${t.key}`} target="_blank" rel="noreferrer" className="font-medium underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{t.key}</a>
      <span className="min-w-0 break-words" style={{ color: 'var(--color-text)' }}>{t.summary}</span>
      <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        {[t.assignee ?? 'unassigned', t.status].filter(Boolean).join(', ')}
        {t.created && created(t.created, now)}
        {t.blockedBy?.length ? <>, blocked by {t.blockedBy.map((k, i) => (
          <span key={k}>{i > 0 && ', '}<a href={`${site}/browse/${k}`} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{k}</a></span>
        ))}</> : null}
      </span>
      {action}
    </li>
  );
}

/** Ask for a summary, for an admin; the board's sprint session once it exists */
function SessionLinks({ serverId, v }: { serverId: string; v: SprintView }) {
  const { user } = useAuth();
  const summary = useSprintSummary(serverId);
  const link = 'text-sm underline-offset-2 hover:underline';
  const sent = summary.isSuccess ? summary.data?.sessionId ?? v.session : undefined;
  return (
    <>
      {v.session && !summary.isSuccess && <Link to={`/servers/${serverId}/sessions/${v.session}`} className={link} style={{ color: 'var(--color-brand-light)' }}>Sprint session</Link>}
      {user?.role === 'admin' && !summary.isSuccess && (
        <button type="button" onClick={() => summary.mutate(v.board.id)} disabled={summary.isPending}
          className="rounded-md border px-2 py-0.5 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60"
          style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' }}>
          {summary.isPending ? 'Sending…' : 'Ask for a summary'}
        </button>
      )}
      {summary.isSuccess && (
        <span className="text-sm" role="status" style={{ color: 'var(--color-text-2)' }}>
          Sent to the sprint session{sent && <>, <Link to={`/servers/${serverId}/sessions/${sent}`} className={link} style={{ color: 'var(--color-brand-light)' }}>open the session</Link></>}
        </span>
      )}
      {summary.isError && <span className="text-sm" role="alert" style={{ color: 'var(--color-error)' }}>{`Not sent: ${summary.error.message}`}</span>}
    </>
  );
}

/** The load of the active sprint and the next ones, on one scale so the capacity marker lines up across them */
function Timeline({ loads, capacity, onGo }: { loads: SprintLoad[]; capacity?: number; onGo: (id: number) => void }) {
  const scale = Math.max(capacity ?? 0, ...loads.map((l) => l.total.points), 1);
  return (
    <ol className="mb-5 grid auto-cols-[minmax(10rem,1fr)] grid-flow-col gap-3 overflow-x-auto pb-1" aria-label="Load of the coming sprints">
      {loads.map((l) => {
        const over = capacity !== undefined && l.total.points > capacity;
        const people = l.people.filter((p) => p.total.points > 0);
        return (
          <li key={l.id}>
            <button type="button" onClick={() => onGo(l.id)} data-testid="sprint-load"
              aria-label={`Open sprint ${l.name}, ${l.total.points}${capacity !== undefined ? ` of ${capacity}` : ''} SP`}
              className="w-full rounded-md border px-3 py-2 text-left focus-visible:outline-2 focus-visible:outline-offset-2"
              style={{ borderColor: l.state === 'active' ? 'var(--color-brand)' : 'var(--color-border)', outlineColor: 'var(--color-brand)' }}>
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{l.name}</span>
                <span className="text-xs tabular-nums" style={{ color: over ? 'var(--color-warn)' : 'var(--color-text-2)' }}>
                  {capacity !== undefined ? `${l.total.points} of ${capacity} SP` : `${l.total.points} SP`}
                </span>
              </span>
              <span className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
                {l.state === 'active' ? 'active' : l.startDate ? `from ${day(l.startDate)}` : 'not started'}
              </span>
              <span aria-hidden="true" className="relative mt-2 block h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-surface-2)' }}>
                <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(l.total.points / scale) * 100}%`, backgroundColor: over ? 'var(--color-warn)' : 'var(--color-brand)' }} />
                {capacity !== undefined && <span className="absolute -inset-y-0.5 w-px" style={{ left: `${(capacity / scale) * 100}%`, backgroundColor: 'var(--color-text-2)' }} />}
              </span>
              <span className="mt-2 block text-xs leading-5" style={{ color: 'var(--color-text-2)' }}>
                {people.length === 0 ? 'Nothing planned yet' : people.slice(0, 4).map((p) => (
                  <span key={p.name} className="flex justify-between gap-2"><span className="truncate">{p.name}</span><span className="tabular-nums">{p.total.points}</span></span>
                ))}
                {people.length > 4 && <span style={{ color: 'var(--color-text-3)' }}>{`and ${people.length - 4} more`}</span>}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** Moves between the board's sprints: the one before, the one after, any of the list */
function SprintNav({ sprints, current, onGo }: { sprints: NonNullable<SprintView['sprints']>; current: number | undefined; onGo: (id: number) => void }) {
  const i = sprints.findIndex((s) => s.id === current);
  const before = i > 0 ? sprints[i - 1] : undefined;
  const after = i >= 0 && i < sprints.length - 1 ? sprints[i + 1] : undefined;
  const button = 'rounded-md border px-2 py-0.5 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-40';
  const style = { borderColor: 'var(--color-border)', color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' };
  const state = { closed: 'closed', active: 'active', future: 'coming' } as const;
  return (
    <span className="flex items-center gap-1.5" role="group" aria-label="Sprints">
      <button type="button" className={button} style={style} disabled={!before} onClick={() => before && onGo(before.id)} aria-label={before ? `Previous sprint, ${before.name}` : 'No previous sprint'}>‹</button>
      <select value={current ?? ''} onChange={(e) => onGo(Number(e.target.value))} aria-label="Sprint"
        className="rounded-md border px-2 py-0.5 text-sm" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text-2)' }}>
        {sprints.map((s) => <option key={s.id} value={s.id}>{`${s.name}, ${state[s.state]}`}</option>)}
      </select>
      <button type="button" className={button} style={style} disabled={!after} onClick={() => after && onGo(after.id)} aria-label={after ? `Next sprint, ${after.name}` : 'No next sprint'}>›</button>
    </span>
  );
}

/** A board: its active sprint by default, or another one chosen in the list */
function Board({ serverId, v, now }: { serverId: string; v: SprintView; now: number }) {
  const [chosen, setChosen] = useState<number | null>(null);
  const other = useSprintById(serverId, v.board.id, chosen);
  const go = (id: number) => setChosen(id === v.sprint?.id ? null : id);
  const shown = chosen !== null && other.data ? other.data : v;
  const nav = v.sprints && v.sprints.length > 1 ? <SprintNav sprints={v.sprints} current={chosen ?? v.sprint?.id} onGo={go} /> : null;
  const back = chosen !== null && v.sprint
    ? <button type="button" onClick={() => setChosen(null)} className="text-sm underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>Back to the active sprint</button>
    : null;
  return (
    <section className="mb-10" aria-labelledby={`sprint-${v.board.id}`} data-testid="sprint-board">
      {chosen !== null && other.isLoading && <p className="mb-2 text-sm" style={{ color: 'var(--color-text-3)' }}>Reading that sprint from Jira…</p>}
      {chosen !== null && other.error && <p role="alert" className="mb-2 text-sm" style={{ color: 'var(--color-error)' }}>{`Could not read that sprint: ${(other.error as Error).message}`}</p>}
      <SprintBody key={shown.sprint?.id ?? 'board'} serverId={serverId} v={shown} now={now} isDefault={shown === v} nav={nav} back={back}
        timeline={v.upcoming && v.upcoming.length > 1 ? <Timeline loads={v.upcoming} capacity={v.capacity} onGo={go} /> : null} />
    </section>
  );
}

function SprintBody({ serverId, v, now, isDefault, nav, back, timeline }: { serverId: string; v: SprintView; now: number; isDefault: boolean; nav: React.ReactNode; back: React.ReactNode; timeline: React.ReactNode }) {
  const [pick, setPick] = useState<{ person: string; column: string }>({ person: '', column: '' });
  const ticketsRef = useRef<HTMLElement>(null);
  const onPick: Pick = (person, column) => {
    setPick({ person, column });
    ticketsRef.current?.scrollIntoView?.({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  };
  const site = new URL(v.url).origin;
  // The tickets' analyses, and Analyse for an admin, on the rows of the backlog and of the sprint's tickets
  const { user } = useAuth();
  const { data: analysed } = useSprintAnalyses(serverId);
  const byKey = new Map<string, JiraAnalysis>((analysed?.analyses ?? []).filter((a) => a.boardId === v.board.id).map((a) => [a.key, a]));
  const action = (key: string) => <TicketAnalysis serverId={serverId} boardId={v.board.id} ticketKey={key} analysis={byKey.get(key)} isAdmin={user?.role === 'admin'} />;
  const withOther = v.totals.other.count > 0;
  const left = isDefault ? daysLeft(v.sprint?.endDate) : null;
  const over = v.capacity !== undefined && v.totals.total.points > v.capacity;
  return (
    <>
      <header className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 id={`sprint-${v.board.id}`} className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>{v.kanban ? v.board.name : v.sprint?.name ?? 'No active sprint'}</h2>
        {v.kanban && <span className="text-sm" style={{ color: 'var(--color-text-2)' }}>{`Kanban: what is open, and what was done in the last ${v.kanban.days} days`}</span>}
        {v.sprint?.startDate && v.sprint.endDate && (
          <span className="text-sm tabular-nums" style={{ color: 'var(--color-text-2)' }}>{`${day(v.sprint.startDate)} to ${day(v.sprint.endDate)}${left ? `, ${left}` : ''}`}</span>
        )}
        {v.capacity !== undefined && (
          <span className="text-sm tabular-nums" style={{ color: over ? 'var(--color-warn)' : 'var(--color-text-3)' }}>{`${v.totals.total.points} of ${v.capacity} SP`}</span>
        )}
        <a href={v.url} target="_blank" rel="noreferrer" className="text-sm underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{v.board.name} in Jira</a>
        {nav}
        {back}
        {isDefault && <SessionLinks serverId={serverId} v={v} />}
      </header>
      {isDefault && <UrgentTickets v={v} analyses={[...byKey.values()]} site={site} now={now} />}
      {isDefault && <BoardAnalyses serverId={serverId} v={v} />}
      {isDefault && !v.kanban && <SprintPlanSection serverId={serverId} v={v} isAdmin={user?.role === 'admin'} />}
      {timeline}
      {v.sprint?.goal && <p className="mb-3 max-w-[72ch] text-sm" style={{ color: 'var(--color-text-2)' }}>{v.sprint.goal}</p>}
      {(v.sprint || v.kanban) && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Story points per person and column, tickets in parentheses</caption>
            <thead>
              <tr className="text-xs" style={{ color: 'var(--color-text-3)' }}>
                <th scope="col" className="py-1 pr-3 text-left font-normal">Person</th>
                {v.columns.map((name, i) => (
                  <th key={name} scope="col" className="px-3 text-right font-normal whitespace-nowrap">
                    <span aria-hidden="true" className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: TONES[i % TONES.length] }} />{name}
                  </th>
                ))}
                {withOther && <th scope="col" className="px-3 text-right font-normal">Other</th>}
                <th scope="col" className="px-3 text-right font-normal">Total</th>
              </tr>
            </thead>
            <tbody>{v.people.map((p) => <Row key={p.name} p={p} columns={v.columns} withOther={withOther} onPick={v.tickets ? onPick : undefined} />)}</tbody>
            <tfoot><Row p={v.totals} columns={v.columns} withOther={withOther} total onPick={v.tickets ? onPick : undefined} /></tfoot>
          </table>
        </div>
      )}
      <p className="mt-2 text-xs" style={{ color: 'var(--color-text-3)' }}>
        Story points, tickets in parentheses.
        {v.previous && ` ${v.previous.name} closed with ${v.columns.map((name, i) => `${name} ${v.previous!.totals.cells[i]?.points ?? 0}`).join(', ')}, total ${v.previous.totals.total.points} SP.`}
      </p>
      {(v.unestimated.length > 0 || v.blocked.length > 0) && (
        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          {v.unestimated.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{`Without estimate (${v.unestimated.length})`}</h3>
              <ul className="mt-1 text-sm">{v.unestimated.map((t) => <Ticket key={t.key} t={t} site={site} now={now} action={action(t.key)} />)}</ul>
            </div>
          )}
          {v.blocked.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{`Blocked (${v.blocked.length})`}</h3>
              <ul className="mt-1 text-sm">{v.blocked.map((t) => <Ticket key={t.key} t={t} site={site} now={now} action={action(t.key)} />)}</ul>
            </div>
          )}
        </div>
      )}
      {v.tickets && <TicketsSection ref={ticketsRef} tickets={v.tickets} columns={withOther ? [...v.columns, 'Other'] : v.columns} site={site} pick={pick} onPick={setPick} now={now} action={action} sprintStart={v.sprint?.startDate} />}
      {isDefault && v.backlog && <BacklogSection backlog={v.backlog} site={site} capacity={v.capacity} now={now} action={action} sprintStart={v.sprint?.startDate} />}
    </>
  );
}

interface UrgentRow { key: string; summary: string; where: string; analysis?: boolean; u: Urgency }

/**
 * What cannot wait, from the active sprint, the backlog and the incoming tickets: expedite, a due date passed or within
 * 2 days, a priority of 1 or down to the board's `urgentPriority`. The most urgent first; nothing when nothing is.
 */
export function UrgentTickets({ v, analyses, site, now }: { v: SprintView; analyses: JiraAnalysis[]; site: string; now: number }) {
  const rows = new Map<string, UrgentRow>();
  const level = v.board.urgentPriority;
  for (const t of v.tickets ?? []) {
    const u = urgencyOf({ priority: t.priority, dueDate: t.duedate, expedite: t.expedite, done: t.done }, now, level);
    if (u.urgent) rows.set(t.key, { key: t.key, summary: t.summary, where: ['in the sprint', t.status, t.assignee ?? 'unassigned'].filter(Boolean).join(', '), u });
  }
  for (const t of v.backlog?.items ?? []) {
    const u = urgencyOf({ priority: t.priority, dueDate: t.duedate, expedite: t.expedite }, now, level);
    if (u.urgent && !rows.has(t.key)) rows.set(t.key, { key: t.key, summary: t.summary, where: ['in the backlog', t.status, t.assignee].filter(Boolean).join(', '), u });
  }
  for (const a of analyses) {
    if (!['pending', 'failed', 'queued'].includes(a.status)) continue;
    const u = urgencyOf(a.urgency, now, level);
    if (u.urgent && !rows.has(a.key)) rows.set(a.key, { key: a.key, summary: a.title, where: a.status === 'queued' ? 'incoming, being analysed' : 'incoming, to decide', analysis: true, u });
  }
  const list = [...rows.values()].sort((a, b) => byUrgency(a.u, b.u));
  if (!list.length) return null;
  return (
    <section className="mb-6 max-w-5xl border-l-2 pl-3" style={{ borderColor: 'var(--color-warn)' }} aria-labelledby={`urgent-${v.board.id}`}>
      <h3 id={`urgent-${v.board.id}`} className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>
        Urgent <span className="text-sm font-normal tabular-nums" style={{ color: 'var(--color-text-3)' }}>{list.length}</span>
      </h3>
      <ul className="mt-1">
        {list.map((r) => (
          <li key={r.key} className="border-t py-1.5 text-sm first:border-t-0" style={{ borderColor: 'var(--color-border)' }} data-testid="urgent-ticket">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <a href={`${site}/browse/${r.key}`} target="_blank" rel="noreferrer" className="font-medium tabular-nums underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{r.key}</a>
              <span className="min-w-0 break-words" style={{ color: 'var(--color-text)' }}>{r.summary}</span>
            </span>
            <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs">
              <span className="font-medium" style={{ color: 'var(--color-warn)' }}>{r.u.reasons.join(', ')}</span>
              {r.analysis
                ? <Link to={`?analysis=${encodeURIComponent(r.key)}`} replace preventScrollReset className="underline-offset-2 hover:underline" style={{ color: 'var(--color-text-3)' }}>{r.where}</Link>
                : <span style={{ color: 'var(--color-text-3)' }}>{r.where}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The board's incoming tickets (ADR-034 step 3a), with the coming sprints to place them in */
function BoardAnalyses({ serverId, v }: { serverId: string; v: SprintView }) {
  const { user } = useAuth();
  const { data, dataUpdatedAt } = useSprintAnalyses(serverId);
  const analyses = (data?.analyses ?? []).filter((a) => a.boardId === v.board.id);
  const sprints = (v.sprints ?? []).filter((s) => s.state !== 'closed').map((s) => s.name);
  return <IncomingTickets serverId={serverId} analyses={analyses} isAdmin={user?.role === 'admin'} sprints={sprints} now={dataUpdatedAt} urgentPriority={v.board.urgentPriority} />;
}

/** The operator's tickets on no board followed (ADR-034 step 5), once above the boards */
function MyJiraTickets({ serverId, urgentPriority }: { serverId: string; urgentPriority?: number }) {
  const { user } = useAuth();
  const { data, dataUpdatedAt } = useSprintAnalyses(serverId);
  const mine = (data?.analyses ?? []).filter((a) => a.boardId === 0);
  return <IncomingTickets serverId={serverId} analyses={mine} isAdmin={user?.role === 'admin'} sprints={[]} now={dataUpdatedAt} title="My Jira tickets" urgentPriority={urgentPriority} />;
}

const who = (t: SprintTicket) => t.assignee ?? 'Unassigned';

/** Text without case nor accents, for the search */
const plain = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/** Whether a ticket matches the search: its key, summary, assignee, status and parent */
function matches(t: SprintTicket & { parent?: { key: string } }, query: string): boolean {
  const q = plain(query.trim());
  if (!q) return true;
  return plain([t.key, t.summary, t.assignee ?? 'unassigned', t.status ?? '', t.parent?.key ?? ''].join(' ')).includes(q);
}

/** A creation date range: from and to (YYYY-MM-DD, both days included), either one open */
type Range = { from: string; to: string };
const inRange = (created: string | undefined, r: Range) => {
  if (!r.from && !r.to) return true;
  // A ticket whose creation date is unknown is not said to be in a range
  const day = created?.slice(0, 10);
  return !!day && (!r.from || day >= r.from) && (!r.to || day <= r.to);
};

/** Ranges proposed as Grafana does, by days from today; `custom` opens the two dates */
const PRESETS: Array<{ id: string; label: string; from?: number; to?: number }> = [
  { id: 'any', label: 'Created any time' },
  { id: '7d', label: 'Created in the last 7 days', from: 7 },
  { id: '14d', label: 'Created in the last 14 days', from: 14 },
  { id: '30d', label: 'Created in the last 30 days', from: 30 },
  { id: '90d', label: 'Created in the last 90 days', from: 90 },
  { id: 'old30', label: 'Created over 30 days ago', to: 30 },
  { id: 'old90', label: 'Created over 90 days ago', to: 90 },
  { id: 'custom', label: 'Custom range…' },
];
const daysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);

/** The creation dates a list is narrowed to: a proposed range, or two dates */
function Created({ value, onChange, label, sprintStart }: { value: Range & { preset?: string }; onChange: (r: Range & { preset: string }) => void; label: string; sprintStart?: string }) {
  const preset = value.preset ?? 'any';
  const select = 'rounded-md border px-2 py-0.5 text-xs';
  const style = { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text-2)' };
  // Since the sprint shown started, on a board with sprints
  const presets = sprintStart ? [PRESETS[0]!, { id: 'sprint', label: 'Created since the sprint started' }, ...PRESETS.slice(1)] : PRESETS;
  const choose = (id: string) => {
    if (id === 'sprint' && sprintStart) {
      onChange({ preset: id, from: sprintStart.slice(0, 10), to: '' });
      return;
    }
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    // Computed when chosen: the range a preset means is today's
    if (id === 'custom') onChange({ ...value, preset: id });
    else onChange({ preset: id, from: p.from !== undefined ? daysAgo(p.from) : '', to: p.to !== undefined ? daysAgo(p.to) : '' });
  };
  return (
    <span className="flex flex-wrap items-center gap-1 text-xs" role="group" aria-label={label} style={{ color: 'var(--color-text-3)' }}>
      <select value={preset} onChange={(e) => choose(e.target.value)} aria-label={`${label}: range`} className={select} style={style}>
        {presets.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      {preset === 'custom' && (
        <>
          <input type="date" value={value.from} max={value.to || undefined} onChange={(e) => onChange({ ...value, preset, from: e.target.value })} aria-label={`${label}: from`} className={select} style={style} />
          to
          <input type="date" value={value.to} min={value.from || undefined} onChange={(e) => onChange({ ...value, preset, to: e.target.value })} aria-label={`${label}: to`} className={select} style={style} />
        </>
      )}
    </span>
  );
}

/** The search field of a list's filters */
function Search({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Search" aria-label={label}
      className="w-36 min-w-0 rounded-md border px-2 py-0.5 text-xs sm:w-44" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }} />
  );
}

function SprintTicketRow({ t, site, now, action }: { t: BoardTicket; site: string; now: number; action?: React.ReactNode }) {
  return (
    <li className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-3 border-t py-1.5" style={{ borderColor: 'var(--color-border)' }} data-testid="sprint-ticket">
      <a href={`${site}/browse/${t.key}`} target="_blank" rel="noreferrer" className="font-medium underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{t.key}</a>
      <span className="min-w-0">
        <span className="break-words" style={{ color: 'var(--color-text)' }}>{t.summary}</span>
        {t.expedite && <span className="ml-2 rounded px-1.5 text-xs font-medium" style={{ backgroundColor: 'var(--color-warn-dim)', color: 'var(--color-warn)' }}>expedite</span>}
        <span className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
          {[who(t), t.status && t.status !== t.column ? `${t.column}, ${t.status}` : t.column, t.priority].filter(Boolean).join(', ')}
          {t.created && created(t.created, now)}
          {t.parent && <>, under <a href={`${site}/browse/${t.parent.key}`} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }} title={t.parent.summary}>{t.parent.key}</a></>}
          {t.blockedBy?.length ? <span style={{ color: 'var(--color-warn)' }}>, blocked by {t.blockedBy.map((k, i) => (
            <span key={k}>{i > 0 && ', '}<a href={`${site}/browse/${k}`} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{k}</a></span>
          ))}</span> : null}
        </span>
        {action && <span className="block">{action}</span>}
      </span>
      <span className="tabular-nums text-right whitespace-nowrap" style={{ color: t.points === undefined ? 'var(--color-text-3)' : 'var(--color-text)' }}>{t.points === undefined ? 'no estimate' : `${t.points} SP`}</span>
    </li>
  );
}

/** Every ticket of the sprint shown, by column and person; a number of the table picks both */
function TicketsSection({ ref, tickets, columns, site, pick, onPick, now, action, sprintStart }: {
  ref: React.Ref<HTMLElement>; tickets: BoardTicket[]; columns: string[]; site: string; now: number; action?: (key: string) => React.ReactNode; sprintStart?: string;
  pick: { person: string; column: string }; onPick: (p: { person: string; column: string }) => void;
}) {
  const [all, setAll] = useState(false);
  const [query, setQuery] = useState('');
  const [range, setRange] = useState<Range & { preset?: string }>({ from: '', to: '' });
  const people = [...new Set(tickets.map(who))].sort((a, b) => (a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b)));
  const passes = (t: BoardTicket) => (!pick.person || who(t) === pick.person) && matches(t, query) && inRange(t.created, range);
  const items = tickets.filter((t) => passes(t) && (!pick.column || t.column === pick.column));
  const shown = all ? items : items.slice(0, SHOWN);
  const points = Math.round(items.reduce((n, t) => n + (t.points ?? 0), 0) * 10) / 10;
  const chip = (active: boolean) => (active
    ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-text)', outlineColor: 'var(--color-brand)' }
    : { borderColor: 'var(--color-border)', color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' });
  return (
    <section ref={ref} className="mt-8 scroll-mt-4" aria-label="Tickets of the sprint">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>Tickets</h3>
        <span className="text-sm tabular-nums" style={{ color: 'var(--color-text-2)' }}>{`${items.length} tickets, ${points} SP`}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2" role="group" aria-label="Column">
        {['', ...columns].map((c) => (
          <button key={c || 'all'} type="button" aria-pressed={pick.column === c} onClick={() => onPick({ ...pick, column: c })}
            className="rounded-full border px-2.5 py-0.5 text-xs focus-visible:outline-2 focus-visible:outline-offset-2" style={chip(pick.column === c)}>
            {`${c || 'All'} (${tickets.filter((t) => passes(t) && (!c || t.column === c)).length})`}
          </button>
        ))}
        <select value={pick.person} onChange={(e) => onPick({ ...pick, person: e.target.value })} aria-label="Person"
          className="rounded-md border px-2 py-0.5 text-xs" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text-2)' }}>
          <option value="">Everyone</option>
          {people.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <Search value={query} onChange={setQuery} label="Search the sprint's tickets" />
        <Created value={range} onChange={setRange} label="Tickets created" sprintStart={sprintStart} />
      </div>
      {items.length === 0
        ? <p className="mt-3 text-sm" style={{ color: 'var(--color-text-3)' }}>{query.trim() || range.from || range.to ? 'No ticket matches these filters.' : 'No ticket here.'}</p>
        : <ul className="mt-2 text-sm">{shown.map((t) => <SprintTicketRow key={t.key} t={t} site={site} now={now} action={action?.(t.key)} />)}</ul>}
      {!all && items.length > SHOWN && (
        <button type="button" onClick={() => setAll(true)} className="mt-2 text-sm underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{`Show all ${items.length}`}</button>
      )}
    </section>
  );
}

const DUE_SOON_DAYS = 14;
type Filter = 'all' | 'unestimated' | 'expedite' | 'due' | 'hierarchy';
const FILTERS: Array<{ id: Filter; label: string; test: (t: BacklogTicket, now: number) => boolean }> = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'unestimated', label: 'Without estimate', test: (t) => t.points === undefined },
  { id: 'expedite', label: 'Expedite', test: (t) => !!t.expedite },
  { id: 'due', label: 'Due soon', test: (t, now) => !!t.duedate && Date.parse(t.duedate) - now <= DUE_SOON_DAYS * 86_400_000 },
  { id: 'hierarchy', label: 'Hierarchy to fix', test: (t) => !!t.noInitiative || !!t.onInitiative },
];

/** Days, weeks or months since a date, short */
function age(iso: string, now: number): string {
  const days = Math.max(0, Math.floor((now - Date.parse(iso)) / 86_400_000));
  return days < 14 ? `${days}d` : days < 90 ? `${Math.floor(days / 7)}w` : `${Math.floor(days / 30)}mo`;
}

/** The creation date and the age: « created 12 Sep, 5w ago » */
const created = (iso: string, now: number) => (Number.isNaN(Date.parse(iso)) ? '' : `, created ${new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${age(iso, now)} ago`);

function BacklogRow({ t, site, now, action }: { t: BacklogTicket; site: string; now: number; action?: React.ReactNode }) {
  const due = t.duedate ? Date.parse(t.duedate) - now : null;
  const late = due !== null && due < 0;
  const soon = due !== null && due <= DUE_SOON_DAYS * 86_400_000;
  const meta = [t.assignee ?? 'unassigned', t.priority, t.status].filter(Boolean).join(', ');
  return (
    <li className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-3 border-t py-1.5" style={{ borderColor: 'var(--color-border)' }} data-testid="backlog-ticket">
      <a href={`${site}/browse/${t.key}`} target="_blank" rel="noreferrer" className="font-medium underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{t.key}</a>
      <span className="min-w-0">
        <span className="break-words" style={{ color: 'var(--color-text)' }}>{t.summary}</span>
        {t.expedite && <span className="ml-2 rounded px-1.5 text-xs font-medium" style={{ backgroundColor: 'var(--color-warn-dim)', color: 'var(--color-warn)' }}>expedite</span>}
        <span className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
          {meta}
          {t.duedate && <span style={{ color: late ? 'var(--color-error)' : soon ? 'var(--color-warn)' : undefined }}>{`, ${late ? 'was due' : 'due'} ${day(t.duedate)}`}</span>}
          {t.created && created(t.created, now)}
          {t.parent && <>, under <a href={`${site}/browse/${t.parent.key}`} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }} title={t.parent.summary}>{t.parent.key}</a></>}
          {t.noInitiative && <span style={{ color: 'var(--color-warn)' }}>, Epic without initiative</span>}
          {t.onInitiative && <span style={{ color: 'var(--color-warn)' }}>, on an initiative, not an Epic</span>}
        </span>
        {action && <span className="block">{action}</span>}
      </span>
      <span className="tabular-nums text-right whitespace-nowrap" style={{ color: t.points === undefined ? 'var(--color-text-3)' : 'var(--color-text)' }}>{t.points === undefined ? 'no estimate' : `${t.points} SP`}</span>
    </li>
  );
}

/** The tickets in no sprint (ADR-034 step 1b), in Jira's order, filtered by what the planning looks for */
function BacklogSection({ backlog, site, capacity, now, action, sprintStart }: { backlog: SprintBacklog; site: string; capacity?: number; now: number; action?: (key: string) => React.ReactNode; sprintStart?: string }) {
  const [filter, setFilter] = useState<Filter>('all');
  const [person, setPerson] = useState('');
  const [query, setQuery] = useState('');
  const [range, setRange] = useState<Range & { preset?: string }>({ from: '', to: '' });
  const [all, setAll] = useState(false);
  const people = [...new Set(backlog.items.map((t) => t.assignee ?? 'unassigned'))].sort();
  const test = FILTERS.find((f) => f.id === filter)!.test;
  const passes = (t: BacklogTicket) => (!person || (t.assignee ?? 'unassigned') === person) && matches(t, query) && inRange(t.created, range);
  const items = backlog.items.filter((t) => test(t, now) && passes(t));
  const shown = all ? items : items.slice(0, SHOWN);
  const sprints = capacity ? Math.round((backlog.total.points / capacity) * 10) / 10 : null;
  return (
    <section className="mt-8" aria-label="Backlog">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text)' }}>Backlog</h3>
        <span className="text-sm tabular-nums" style={{ color: 'var(--color-text-2)' }}>
          {`${backlog.total.count} tickets, ${backlog.total.points} SP estimated${sprints !== null ? `, about ${sprints} sprint${sprints === 1 ? '' : 's'} at ${capacity} SP` : ''}`}
          {backlog.capped && ', the first ones only'}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2" role="group" aria-label="Show">
        {FILTERS.map((f) => {
          const n = backlog.items.filter((t) => f.test(t, now) && passes(t)).length;
          return (
            <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}
              className="rounded-full border px-2.5 py-0.5 text-xs focus-visible:outline-2 focus-visible:outline-offset-2"
              style={filter === f.id ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-text)', outlineColor: 'var(--color-brand)' } : { borderColor: 'var(--color-border)', color: 'var(--color-text-2)', outlineColor: 'var(--color-brand)' }}>
              {`${f.label} (${n})`}
            </button>
          );
        })}
        <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Person"
          className="rounded-md border px-2 py-0.5 text-xs" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text-2)' }}>
          <option value="">Everyone</option>
          {people.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <Search value={query} onChange={setQuery} label="Search the backlog" />
        <Created value={range} onChange={setRange} label="Backlog created" sprintStart={sprintStart} />
      </div>
      {items.length === 0
        ? <p className="mt-3 text-sm" style={{ color: 'var(--color-text-3)' }}>{query.trim() || range.from || range.to ? 'No ticket matches these filters.' : 'No ticket here.'}</p>
        : <ul className="mt-2 text-sm">{shown.map((t) => <BacklogRow key={t.key} t={t} site={site} now={now} action={action?.(t.key)} />)}</ul>}
      {!all && items.length > SHOWN && (
        <button type="button" onClick={() => setAll(true)} className="mt-2 text-sm underline-offset-2 hover:underline" style={{ color: 'var(--color-brand-light)' }}>{`Show all ${items.length}`}</button>
      )}
    </section>
  );
}

const isView = (b: SprintBoard): b is SprintView => !('error' in b);

export function SprintTab({ serverId }: { serverId: string }) {
  const { data, error, isLoading, dataUpdatedAt } = useSprint(serverId);
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Reading the sprint from Jira…</p>;
  if (error) return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Could not read the sprint: {(error as Error).message}</p>;
  if (!data?.configured) return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>This server follows no Jira board: add one to its SDLC config (jira.boards).</p>;
  return (
    <div>
      <MyJiraTickets serverId={serverId} urgentPriority={widestUrgentPriority(data.boards)} />
      {data.boards.map((b) => isView(b)
        ? <Board key={b.board.id} serverId={serverId} v={b} now={dataUpdatedAt} />
        : <p key={b.board.id} role="alert" className="mb-6 text-sm" style={{ color: 'var(--color-error)' }}>{`${b.board.name}: ${b.error}`}</p>)}
    </div>
  );
}
